import { Edge } from "@atlaskit/pragmatic-drag-and-drop-hitbox/types";
import { combine } from "@atlaskit/pragmatic-drag-and-drop/combine";
import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { DropIndicator } from "app/components/DropIndicator";
import { BoardViewType } from "app/types";
import React, { useRef } from "react";
import AddTicket from "../../../../components/AddTicket";
import { attachClickOutsideListener } from "../../../../components/Settings/helper";
import { TicketGroup } from "../../../../types";
import AddTaskButton from "./AddTaskButton";
import DraggableColumn from "./DraggableColumn";
import GroupHeader from "./GroupHeader";

interface ColumnProps {
  group: TicketGroup;
  onDragEnd?: (
    ticketId: string,
    sourceStatus: string,
    targetStatus: string
  ) => void;
  showAddTaskIcon: boolean;
  onCancel: () => void;
  selectedType: string;
}

export default function Index({
  group,
  onDragEnd,
  onCancel,
  selectedType,
  showAddTaskIcon,
}: ColumnProps) {
  const dropzoneRef = React.useRef<HTMLDivElement>(null);
  const [isDraggingOver, setIsDraggingOver] = React.useState(false);
  const [dropEdge, setDropEdge] = React.useState<Edge | null>(null);
  const addTicketRef = useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!dropzoneRef.current) return;

    return combine(
      dropTargetForElements({
        element: dropzoneRef.current,
        onDrag: ({ location }) => {
          setIsDraggingOver(true);
          // Determine edge based on drag location
          setDropEdge("bottom");
        },
        onDragLeave: () => {
          setIsDraggingOver(false);
          setDropEdge(null);
        },
        onDrop: ({ source }) => {
          setIsDraggingOver(false);
          setDropEdge(null);
          const data = source.data;
          if (data && "ticketId" in data && "status" in data && onDragEnd) {
            onDragEnd(
              data.ticketId as string,
              data.status as string,
              group?.groupName
            );
          }
        },
      })
    );
  }, [group?.groupName, onDragEnd]);

  return (
    <div className="flex h-full flex-col">
      <GroupHeader group={group} />
      <div
        ref={dropzoneRef}
        className="flex flex-col justify-start mt-[16px] gap-[12px] h-full  overflow-y-auto relative"
      >
        {showAddTaskIcon && (
          <div ref={addTicketRef}>
            <AddTicket
              type={BoardViewType.KANBAN}
              onCancel={() => onCancel()}
              group={group}
            />
          </div>
        )}
        {isDraggingOver && dropEdge && (
          <DropIndicator edge={dropEdge} gap="12px" />
        )}
        {group?.tickets?.slice(0, group?.ticketLimit).map((ticket) => (
          <DraggableColumn
            key={ticket?._id}
            ticket={ticket}
            status={group?.groupName}
          />
        ))}
      </div>
      {/* AddTaskButton moved inside the scrollable container */}
      <div className="fixed bottom-0  pt-2 pb-4mt-auto bg-[#F4F5F7] w-full z-10">
        <AddTaskButton
          groupId={group?._id}
          selectedType={selectedType as string}
          dropzoneRef={dropzoneRef}
          showAddTaskIcon={showAddTaskIcon}
        />
      </div>
    </div>
  );
}
